import { Prisma, SupplierAttribute } from '@prisma/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { SupplierAttributeDetail, UpsertSupplierAttributeArgs } from '@app/domain-interfaces';
import { CommonFindResult } from '@app/infra-interfaces';
import { Axios } from '@app/web-components';

const SUPPLIER_ATTR_ALL_QUERY_KEY = 'SUPPLIER_ATTR_ALL_QUERY_KEY';

const supplierAttributeKeys = {
  all: [SUPPLIER_ATTR_ALL_QUERY_KEY] as const,
  lists: () => [...supplierAttributeKeys.all, 'list'] as const,
  list: (filters: object) => [...supplierAttributeKeys.lists(), filters] as const,
  details: () => [...supplierAttributeKeys.all, 'detail'] as const,
  detail: (id: number) => [...supplierAttributeKeys.details(), id] as const,
};

export const useSupplierAttribute = (id: number) => {
  const axios = Axios.useAxiosApi();

  return useQuery<SupplierAttributeDetail>({
    queryKey: supplierAttributeKeys.detail(id),
    queryFn: async () => {
      const { data } = await axios.get(`supplier-attributes/${id}`);
      return data;
    },
  });
};

export const useSupplierAttributes = (query: Prisma.SupplierAttributeFindManyArgs) => {
  const axios = Axios.useAxiosApi();

  return useQuery<CommonFindResult<SupplierAttributeDetail>>({
    queryKey: supplierAttributeKeys.list(query),
    queryFn: async () => {
      const { data } = await axios.post('supplier-attributes/find', query);
      return data;
    },
  });
};

export const useCreateSupplierAttribute = () => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: Omit<UpsertSupplierAttributeArgs, 'userId'>) => {
      return axios.post<SupplierAttribute>('/supplier-attributes', dto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierAttributeKeys.all });
    },
  });
};

export const useEditSupplierAttribute = () => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: Omit<UpsertSupplierAttributeArgs, 'userId'>) => {
      return axios.patch<SupplierAttribute>('/supplier-attributes', dto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierAttributeKeys.all });
    },
  });
};

export const useDeleteSupplierAttribute = () => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => {
      return axios.delete<SupplierAttribute>(`/supplier-attributes/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierAttributeKeys.all });
    },
  });
};
